import { useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API } from '../../utils/amplifyWrapper';
import { assertFeatureCreditsOk } from '../organisation/featureCreditsGuard';

// Reuse the shared S3 PUT helper.
export { uploadToS3 } from '../ai-voices/useClonedVoice';

// ── List dubbing jobs (newest-first) ───────────────────────────────────────────
export const fetchDubbingJobs = async (org_id, integration_id) => {
    const res = await API.get('main', `/organisation/${org_id}/integration/${integration_id}/dubbing/jobs`);
    return res.data || [];
};

const ACTIVE_STATUSES = ['pending', 'processing'];

export const useDubbingJobs = ({ org_id, integration_id }) => {
    const queryClient = useQueryClient();
    // Detect a job settling to refresh the credit balance — dubbing is billed on completion
    // (summed across all dubbed languages).
    const prevActiveRef = useRef(null);
    return useQuery(
        ['dubbing_jobs', org_id, integration_id],
        () => fetchDubbingJobs(org_id, integration_id),
        {
            enabled: !!(org_id && integration_id),
            staleTime: 30 * 1000,
            // Poll while any job is still in flight; stop once every job has settled.
            refetchInterval: (data) =>
                Array.isArray(data) && data.some(j => ACTIVE_STATUSES.includes(j.status)) ? 5000 : false,
            onSuccess: (data) => {
                const active = Array.isArray(data) ? data.filter(j => ACTIVE_STATUSES.includes(j.status)).length : 0;
                if (prevActiveRef.current !== null && active < prevActiveRef.current) {
                    queryClient.invalidateQueries(['organisation_usage', org_id]);
                }
                prevActiveRef.current = active;
            },
        }
    );
};

// ── Create a dubbing job ────────────────────────────────────────────────────────
export const useCreateDubbingJob = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ org_id, integration_id, body }) => {
            const res = await API.post('main', `/organisation/${org_id}/integration/${integration_id}/dubbing/start`, { body });
            assertFeatureCreditsOk(res);
            return { res, org_id, integration_id };
        },
        {
            onSuccess: ({ org_id, integration_id }) => {
                queryClient.invalidateQueries(['dubbing_jobs', org_id, integration_id]);
            },
        }
    );
};

// ── Delete a dubbing job (also purges its S3 artifacts server-side) ─────────────
export const useDeleteDubbingJob = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ org_id, integration_id, job_id }) => {
            const res = await API.del('main', `/organisation/${org_id}/integration/${integration_id}/dubbing/jobs/${job_id}`, {
                headers: { 'Content-Type': 'application/json' },
            });
            return { res, org_id, integration_id };
        },
        {
            onSuccess: ({ org_id, integration_id }) => {
                queryClient.invalidateQueries(['dubbing_jobs', org_id, integration_id]);
            },
        }
    );
};

// Presigned upload for a dubbing source file. Lands in dubbing/{org}/{int}/ in the public
// (DUBBING) bucket so the external /isolate + /dubbing services can read it. Accepts audio
// or video. Returns { signed_url, url } where `url` is the S3 key to pass as sourceUrl.
export const fetchDubbingPresignedUrl = async ({ org_id, integration_id, fileName, contentType }) => {
    const response = await API.get(
        'main',
        `/organisation/${org_id}/integration/${integration_id}/audio/signed-url`
            + `?file_name=${encodeURIComponent(fileName)}`
            + `&content_type=${encodeURIComponent(contentType)}`
            + `&file_name_prefix=dubbing&file_type=dubbing`,
        { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data; // { signed_url, url }
};
